import React, { useEffect, useRef } from 'react';
import * as R from 'ramda';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// Utils
import { bool, func, number, shape, string } from 'prop-types';
import { useIntersect } from 'hooks';
import { CardBody, CardHeader } from './card-components';

const MyAuthCard = ({ card, index, setCardHeight, viewBy }) => {
  const {
    active,
    endDate,
    exchange,
    inventoryOwner,
    lastUsed,
    limitMet,
    limitType,
    limitVolume,
    remainingVolume,
    location,
    product,
    productAuthorizationId,
    sequenced,
    unlimited,
  } = card;
  // any function ran here will be evaluated on every rerender, regardless if hideCard is true or false
  // please put any specific logic for components inside the component to ensure it is only ran when hideCard is false
  // this is especially important when 188+ cards are rendered, as you are evaluating a signficant amount of logic
  const { entry, setNode } = useIntersect({ rootMargin: '300px' });
  const node = useRef();

  useEffect(() => {
    setNode(node.current);
  }, [setNode]);

  // since the first 10 cards are always rendered, we can pull the card height off the first one right away
  // allows us to make future changes to the cards without worrying about keeping the card heights
  useEffect(() => {
    if (index === 0) setCardHeight(node.current.getBoundingClientRect().height);
  }, [index, setCardHeight]);

  // isIntersecting fixes a mobile bug that causes cards just outside the parent to not render
  const { intersectionRatio, isIntersecting } = entry;
  // show the first 10 cards on initial render, when intersectionRatio is undefined
  // otherwise, only show if intersectionRatio > 0
  // keeps from showing blank cards on initial load
  const hideCard =
    (R.isNil(intersectionRatio) && index > 10) || (intersectionRatio === 0 && !isIntersecting);

  // leaving this function here since it is used in body and header
  const cardHeaderInfo = R.cond([
    [
      R.equals('inventory-owner'),
      R.always({
        subtitle: location.nameShort,
        title: product.nameShort,
      }),
    ],
    [
      R.equals('location'),
      R.always({
        subtitle: inventoryOwner?.nameShort,
        title: product.nameShort,
      }),
    ],
    [
      R.equals('product'),
      R.always({
        subtitle: location.nameShort,
        title: inventoryOwner?.nameShort,
      }),
    ],
  ])(viewBy);

  return (
    <Card
      ref={node}
      sx={{
        height: 'auto',
      }}
      variant="loading"
    >
      <CardContent variant="loading">
        {!hideCard && (
          <>
            <CardHeader
              proprietary={product?.proprietary}
              sequenced={sequenced}
              subtitle={cardHeaderInfo?.subtitle}
              title={cardHeaderInfo?.title}
            />
            <CardBody
              active={active}
              endDate={endDate}
              exchange={exchange}
              lastUsed={lastUsed}
              limitMet={limitMet}
              limitType={limitType}
              limitVolume={limitVolume}
              productAuthorizationId={productAuthorizationId}
              remainingVolume={remainingVolume}
              twoPartyExchangeInfo={cardHeaderInfo}
              unlimited={unlimited}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

MyAuthCard.propTypes = {
  card: shape({
    active: bool,
    endDate: string,
    exchange: shape({}),
    lastUsed: string,
    limitMet: bool,
    limitType: string,
    limitVolume: number,
    location: shape({}),
    product: shape({}),
    productAuthorizationId: number,
    sequenced: bool,
    supplier: shape({}),
    unlimited: bool,
  }).isRequired,
  index: number.isRequired,
  setCardHeight: func.isRequired,
  viewBy: string.isRequired,
};

export default MyAuthCard;
